import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPaginatedParts,
  getPaginatedPartsWithDevices,
  getPaginatedPartsWithLocations,
  getPart,
  getPartsByPartModelID,
  getPartsBySearch,
} from "@/api/resources/parts/api";
import i18n from "@/providers/i18n";
import { type QueryPaginationFilters } from "@/utils/pagination";

import type { SearchOptionFilters } from "@/utils/api";

const entity = i18n.t("part");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("parts");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const partsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "parts"],
  lists: () => [...partsQueryKeys.all(), "list"],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partsQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listPaginatedWithDevices: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partsQueryKeys.lists(),
    "paginatedWithDevices",
    queryKeyPaginationFilters,
  ],
  listPaginatedWithLocations: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partsQueryKeys.lists(),
    "paginatedWithLocations",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: SearchOptionFilters) => [
    ...partsQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...partsQueryKeys.all(), "byId", id],
  listByPartModelID: (partModelID: string, deploymentID: string) => [
    ...partsQueryKeys.all(),
    "byPartModelID",
    partModelID,
    deploymentID,
  ],
};

export const partsQueries = {
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partsQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedParts(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginatedWithDevices: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partsQueryKeys.listPaginatedWithDevices(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartsWithDevices(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginatedWithLocations: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partsQueryKeys.listPaginatedWithLocations(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartsWithLocations(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchOptionFilters) =>
    queryOptions({
      queryKey: [...partsQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getPartsBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...partsQueryKeys.byId(id)],
      queryFn: id ? () => getPart({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  listByPartModelID: (partModelID: string, deploymentID: string) =>
    queryOptions({
      queryKey: [...partsQueryKeys.listByPartModelID(partModelID, deploymentID)],
      queryFn: () => getPartsByPartModelID(partModelID, deploymentID),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
};
